import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, analyzeCaseStream, reprocessCase, getClues } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { BrainPanel } from "@/components/BrainPanel";
import { NatalieChat } from "@/components/NatalieChat";
import { AddEvidenceDialog } from "@/components/IntakeDialog";
import { BulkIntakeDialog } from "@/components/BulkIntakeDialog";
import { EvidenceViewer } from "@/components/EvidenceViewer";
import { EvidenceTable } from "@/components/EvidenceTable";
import { EnginePulse } from "@/components/EnginePulse";
import { LiveLeadsFeed } from "@/components/LiveLeadsFeed";
import { CaseProgress } from "@/components/CaseProgress";
import { ArrowLeft, Brain, FileText, Loader2, MapPin, ListChecks, Boxes, RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const TONE_STYLES = {
  grave: "text-monk-critical border-monk-critical/40 bg-monk-critical/10",
  urgent: "text-monk-critical border-monk-critical/40 bg-monk-critical/10",
  focused: "text-monk-blue border-monk-blue/40 bg-monk-blue/10",
  measured: "text-neutral-300 border-white/15 bg-white/5",
};

export default function CaseDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [flash, setFlash] = useState("");
  const [phase, setPhase] = useState("");
  const [viewer, setViewer] = useState(null);
  const [reprocessing, setReprocessing] = useState(false);
  const [clues, setClues] = useState([]);
  const [pulse, setPulse] = useState(null);

  const load = () => api.get(`/cases/${id}`).then((r) => setData(r.data));
  const loadClues = () => getClues(id).then((r) => { setClues(r.clues || []); setPulse(r.pulse || null); }).catch(() => {});
  useEffect(() => { load(); loadClues(); }, [id]);

  // Auto-refresh while evidence is being triaged / auto-orchestrated OR the case-level
  // reasoning pack is auto-firing, so the record + live clue stream reflect every
  // engine result the moment it lands.
  const evBusy = (data?.evidence || []).some(
    (e) => ["queued", "running"].includes(e.orchestration_status) || e.triage_status === "pending"
  );
  const caseBusy = !!pulse?.running;
  useEffect(() => {
    if (!evBusy && !caseBusy) return;
    const t = setInterval(() => { load(); loadClues(); }, 3000);
    return () => clearInterval(t);
  }, [evBusy, caseBusy]);

  const analyze = async () => {
    setAnalyzing(true); setFlash(""); setPhase("reasoning");
    try {
      await analyzeCaseStream(
        id,
        (prose) => setFlash(prose),
        (res) => {
          setData((d) => ({ ...d, case: { ...d.case, analysis: res.analysis, analyzed_at: res.analyzed_at } }));
          toast.success("Core Brain analysis complete");
        },
        () => setPhase("compiling"),
      );
    } catch { toast.error("Core Brain could not complete the analysis"); }
    finally { setAnalyzing(false); setFlash(""); setPhase(""); }
  };

  const reprocess = async () => {
    setReprocessing(true);
    try {
      const res = await reprocessCase(id);
      if (res.queued > 0) {
        toast.success(`Re-running engines on ${res.queued} evidence item${res.queued === 1 ? "" : "s"}…`);
        await load();
      } else {
        toast.info("Every evidence item already has findings.");
      }
    } catch { toast.error("Could not re-run the engines"); }
    finally { setReprocessing(false); }
  };

  if (!data) return <AppShell><div className="font-mono text-monk-blue text-sm">LOADING CASE FILE…</div></AppShell>;

  const { case: c, evidence } = data;
  const analysis = c.analysis;
  const nextActions = analysis?.next_actions || [];

  return (
    <AppShell>
      <button onClick={() => nav("/")} data-testid="back-btn" className="flex items-center gap-2 text-neutral-400 hover:text-white text-sm mb-6 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Command Center
      </button>

      {/* Case header */}
      <div className="bg-monk-panel border border-white/10 rounded-md p-6 mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[280px]">
            <div className="overline mb-2">{c.case_type?.replace("_", " ")} · {c.status} · {c.priority} priority</div>
            <h1 className="font-heading font-900 text-3xl sm:text-4xl tracking-tighter leading-none mb-3" data-testid="case-detail-title">{c.title}</h1>
            {c.summary_auto && (
              <span className="inline-flex items-center gap-1.5 text-[9px] font-mono px-1.5 py-0.5 rounded-sm border border-monk-blue/40 text-monk-blue bg-monk-blue/10 mb-2" data-testid="summary-auto-badge">
                <Brain className="w-2.5 h-2.5" /> AUTO-GENERATED BY CORE BRAIN
              </span>
            )}
            <p className="text-neutral-300 text-sm max-w-3xl leading-relaxed mb-3">{c.summary}</p>
            <div className="flex items-center gap-4 text-[12px] text-neutral-500 font-mono">
              <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {c.location || "—"}</span>
              <span className="flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> {evidence.length} evidence items</span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-3">
            {analysis?.monk_tone && (
              <span className={`text-[10px] font-mono px-2 py-1 rounded-sm border ${TONE_STYLES[analysis.monk_tone] || TONE_STYLES.measured}`}>
                MONK: {analysis.monk_tone.toUpperCase()}
              </span>
            )}
            <div className="flex items-center gap-2">
              <button onClick={() => nav(`/case/${id}/simulation`)} data-testid="simulation-deck-btn"
                className="flex items-center gap-2 border border-monk-blue/40 text-monk-blue text-sm font-medium rounded-sm px-4 py-2.5 hover:bg-monk-blue/10 transition-colors">
                <Boxes className="w-4 h-4" /> Case Intelligence
              </button>
            </div>
          </div>
        </div>

        {/* Monk's case read */}
        {analysis?.case_read && (
          <div className="mt-5 pt-5 border-t border-white/10">
            <div className="overline mb-2 flex items-center gap-2"><Brain className="w-3.5 h-3.5 text-monk-blue" /> Monk's Read</div>
            <p className="hard-data text-[15px] leading-relaxed text-neutral-100" data-testid="monk-read">{analysis.case_read}</p>
          </div>
        )}
      </div>

      {pulse?.stuck > 0 && (
        <div data-testid="stuck-warning-banner"
          className="mb-6 flex items-center justify-between gap-3 rounded-md border border-monk-warn/40 bg-monk-warn/10 px-4 py-3 flex-wrap">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-4 h-4 text-monk-warn shrink-0" />
            <div className="text-[13px] text-neutral-200">
              <span className="font-medium text-monk-warn">{pulse.stuck} analysis job{pulse.stuck === 1 ? "" : "s"} appear{pulse.stuck === 1 ? "s" : ""} stuck.</span>{" "}
              A restart may have interrupted processing. Re-run the engines to recover them.
            </div>
          </div>
          <button onClick={reprocess} disabled={reprocessing} data-testid="stuck-rerun-btn"
            className="flex items-center gap-2 border border-monk-warn/50 text-monk-warn text-sm rounded-sm px-3 py-2 hover:bg-monk-warn/10 disabled:opacity-60 transition-colors">
            {reprocessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Re-run Engines
          </button>
        </div>
      )}

      <CaseProgress caseId={id} />

      <EnginePulse pulse={pulse} evidence={evidence} analyzed={!!(c.analyzed_at || analysis)} />

      <LiveLeadsFeed clues={clues} running={caseBusy || evBusy}
        onOpenEvidence={(eid) => {
          const idx = evidence.findIndex((e) => e.id === eid);
          if (idx !== -1) setViewer({ evidence: evidence[idx], index: idx });
        }} />

      {/* Unified Evidence Record — dense, searchable. All specialist findings live inside Evidence Detail. */}
      <div className="mb-6">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div className="overline">Unified Evidence Record</div>
          <div className="flex items-center gap-2">
            <button onClick={reprocess} disabled={reprocessing} data-testid="reprocess-btn"
              className="flex items-center gap-2 border border-white/15 text-neutral-300 text-sm rounded-sm px-3 py-2 hover:text-white hover:border-white/30 disabled:opacity-60 transition-colors">
              {reprocessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Re-run Engines
            </button>
            <AddEvidenceDialog caseId={id} onAdded={load} />
            <BulkIntakeDialog caseId={id} onAdded={load} />
          </div>
        </div>
        <EvidenceTable evidence={evidence} caseId={id}
          onOpen={(e, i) => setViewer({ evidence: e, index: i })} onUpdated={load} />
      </div>

      {/* Core Brain + Natalie */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        <div className="xl:col-span-8">
          <div className="overline mb-3">Core Brain Output</div>
          {analyzing && (
            <div className="bg-monk-panel border border-monk-blue/30 rounded-md mb-4 overflow-hidden" data-testid="flash-report">
              <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-monk-blue animate-pulse" />
                <span className="overline text-monk-blue">Live Flash Report</span>
                <span className="ml-auto font-mono text-[10px] text-neutral-500 flex items-center gap-1.5">
                  <Loader2 className="w-3 h-3 animate-spin" /> {phase === "compiling" ? "COMPILING FINDINGS…" : "REASONING…"}
                </span>
              </div>
              <div className="p-4">
                {flash ? (
                  <p className="hard-data text-[14px] leading-relaxed text-neutral-100 whitespace-pre-wrap" data-testid="flash-report-text">
                    {flash}<span className="inline-block w-1.5 h-4 bg-monk-blue ml-0.5 align-middle animate-pulse" />
                  </p>
                ) : (
                  <p className="hunch-data text-[13px]">Monk is opening the case file…</p>
                )}
              </div>
            </div>
          )}
          {analysis ? (
            <>
              <BrainPanel analysis={analysis} />
              {nextActions.length > 0 && (
                <div className="bg-monk-panel border border-white/10 rounded-md mt-4">
                  <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
                    <ListChecks className="w-4 h-4 text-neutral-400" /><span className="overline">Recommended Actions</span>
                  </div>
                  <div className="p-4 space-y-2">
                    {nextActions.map((a, i) => (
                      <div key={i} className="flex items-start gap-2 text-[13px] text-neutral-300" data-testid="next-action">
                        <span className="font-mono text-[11px] text-monk-blue mt-0.5">{String(i + 1).padStart(2, "0")}</span>{a}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            !analyzing && (
            <div className="bg-monk-panel border border-dashed border-white/15 rounded-md p-8 text-center">
              <Brain className="w-8 h-8 text-neutral-600 mx-auto mb-3" />
              <p className="text-neutral-400 text-sm mb-1">Monk analyzes automatically.</p>
              <p className="hunch-data text-[13px]">Ingest evidence and the full engine array + Core Brain synthesis run on their own — the read appears here.</p>
            </div>
            )
          )}
        </div>

        {/* Natalie */}
        <div className="xl:col-span-4">
          <div className="overline mb-3">Natalie · Interface</div>
          <div className="h-[640px]"><NatalieChat caseId={id} /></div>
        </div>
      </div>

      {viewer && (
        <EvidenceViewer
          open={!!viewer}
          onOpenChange={(o) => !o && setViewer(null)}
          index={viewer.index}
          caseId={id}
          evidence={evidence.find((e) => e.id === viewer.evidence.id) || viewer.evidence}
          onUpdated={load}
        />
      )}
    </AppShell>
  );
}
